import type { OutreachCandidate } from '../../types/taOutreach';

/**
 * The keys that say two rows are the same person: every Recruiter id, the public profile link,
 * and only when neither is known, the name.
 */
function keysOf(c: OutreachCandidate): string[] {
  const ids = [c.recruiterId, ...(c.recruiterIds || [])].filter(Boolean).map((id) => `id:${id}`);
  const url = `${c.publicUrl || ''}`.trim().toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');
  const keys = [...ids, ...(url ? [`url:${url}`] : [])];
  return keys.length ? keys : [`name:${c.name.trim().toLowerCase().replace(/\s+/g, ' ')}`];
}

/**
 * Rows of the same person, grouped (Sofia, Oct 28 2026): since the list is the team's (#747), a test
 * recipient sits on the role once per recruiter who added them, and older duplicates predate the
 * team-wide import guard. Rows that share any key are one person. The data is not merged; only
 * what the page counts, lists and acts on.
 */
export function groupByPerson(rows: OutreachCandidate[]): OutreachCandidate[][] {
  const groups: { keys: Set<string>; rows: OutreachCandidate[] }[] = [];
  for (const c of rows) {
    const keys = keysOf(c);
    const hits = groups.filter((g) => keys.some((k) => g.keys.has(k)));
    const into = hits[0] || { keys: new Set<string>(), rows: [] };
    if (!hits.length) groups.push(into);
    for (const other of hits.slice(1)) {
      other.keys.forEach((k) => into.keys.add(k));
      into.rows.push(...other.rows);
      groups.splice(groups.indexOf(other), 1);
    }
    keys.forEach((k) => into.keys.add(k));
    into.rows.push(c);
  }
  return groups.map((g) => g.rows);
}

/** The row to act on for one person: the viewer's own when there is one, else the first. */
export const preferOwn = (rows: OutreachCandidate[], viewer: string) =>
  rows.find((c) => `${c.owner || ''}`.toLowerCase() === viewer) || rows[0];

/** One row per person, the viewer's own preferred. */
export const onePerPerson = (rows: OutreachCandidate[], viewer: string) => groupByPerson(rows).map((g) => preferOwn(g, viewer));
